import { useState, useContext, useEffect } from "react";
import { IPost } from "../../models";
import { Link, useNavigate } from "react-router-dom";
import {
  FaHeart,
  FaComment,
  FaEllipsisH,
  FaMapMarkerAlt,
  FaPencilAlt,
  FaTrashAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import { PostsV2Service } from "../../../../common/services/api/api";
import Context from "../../../../common/components/utils/context";
import { getEditPostUrl } from "../../../../common/components/route/routeHelpers";
import { useOutsideClick } from "../../../../common/components/utils/useOutsideClick";
import { notifcation } from "../../../../common/components/utils/notifications";
import { FaEarthAmericas } from "react-icons/fa6";
import { IModal, Modal } from "../../../../common/components/modal/modal";
import Select from "react-select";
import { Themes } from "../../../../common/enums/themes";
import makeAnimated from "react-select/animated";
import { option } from "../../../contacts/components/groups";
import { IMapPin } from "../../../../common/components/map/models";
import { PostMapView } from "./postMap";

export const PostCard = ({ post, onClick, commentCount }: IPostCard) => {
  const context = useContext(Context);

  const [dropdown, setDropdown] = useState(false);
  const [liked, setLiked] = useState(
    post.userLikes.indexOf(context.account.accountId) !== -1
  );
  const [likes, setLikes] = useState(post.likes);

  const [modal, setModal] = useState<IModal>();
  const [showModal, setShowModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const toggleShowModal = () => setShowModal(!showModal);
  const closeModal = () => setShowModal(false);
  const closeMap = () => setShowMap(false);
  const animatedComponents = makeAnimated();
  const [postSelected, setPostSelected] = useState<string>();

  const options: readonly option[] = context.shapeFiles.map((sf) => ({
    value: sf.id,
    label: sf.displayName,
  }));

  const postService = new PostsV2Service();
  const date = new Date(post.created);

  const handleLikeClick = async () => {
    (await liked) ? postService.unlike(post.id) : postService.like(post.id);
    setLiked(!liked);
    //TODO: We should really be storing things in context and fetching the fresh data
    setLikes(liked ? likes - 1 : likes + 1);
  };

  useEffect(() => {
    setModal({
      show: showModal,
      component: (
        <SingleSelect
          name="shapefile"
          options={options}
          onClose={toggleShowModal}
        />
      ),
    });
  }, [showModal]);

  useEffect(() => {
    if (showMap) {
      setPostSelected(post.id);
      const location: IMapPin = {
        id: post.id,
        latitude: post.latitude,
        longitude: post.longitude,
        altitude: post.altitude,
      };
      setModal({
        show: showMap,
        component: (
          <PostMapView
            locations={[location]}
            selected={setPostSelected}
            setPostSelected={setPostSelected}
            flyTo={postSelected}
            shapeFileId={post.shapeFileId}
            poiFileId={post.poiFileId}
          />
        ),
        size: "lg",
        onClose: () => {
          closeModal();
          closeMap();
        },
      });
    }
  }, [showMap, post]);

  const SingleSelect = (props: any) => {
    const [selectedShapeFile, setSelectedShapeFile] = useState<string>("");

    const onSelectChange = (c: any) => {
      setSelectedShapeFile(c.value);
    };

    const assignShapeFile = async () => {
      try {
        Object.defineProperty(post, "shapeFileId", {
          value: selectedShapeFile,
        });
        const response = await postService.update(post);
        if (response.status === 200) {
          context.setAlert({
            message: "File was successfully linked to the post!",
            theme: Themes.Success,
          });
          setModal({ show: false });
        } else {
          context.setAlert({
            message: "Error linking shape file",
            theme: Themes.Danger,
          });
        }
      } catch (error) {
        context.setAlert({
          message: "Error linking shape file",
          theme: Themes.Danger,
        });
        console.error("Error linking shape file", error);
      }
    };

    return (
      <div className="h-lvh w-full max-w-2xl mx-auto bg-white rounded-xl overflow-hidden shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Assign 2D file to post
          </h2>
          <button
            className="text-gray-500 hover:text-gray-800 focus:outline-none"
            aria-label="Close"
            onClick={props.onClose}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <Select
            components={animatedComponents}
            // isMulti
            id="selectedShapeFile"
            name="selectedShapeFile"
            options={props.options}
            className="basic-multi-select"
            classNamePrefix="select a file..."
            onChange={onSelectChange}
          />
        </div>
        <div className="absolute bottom-36">
          <button
            color="red"
            type="button"
            className="rounded-md text-white bg-blue-600 hover:bg-blue-700  px-10 py-2 mb-4"
            disabled={!selectedShapeFile}
            style={{
              cursor: !selectedShapeFile ? "not-allowed" : "pointer",
            }}
            onClick={assignShapeFile}
          >
            Attribute
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <article className="w-full sm:w-11/12 md:w-2/3 lg:w-7/16 2xl:w-7/16 mx-auto bg-white rounded-xl overflow-hidden shadow-md md:max-w-2xl">
        <img
          className="w-full h-56 object-cover cursor-pointer"
          src={post.pictureURL}
          alt={post.body?.split(". ", 1)[0]}
          onClick={() => onClick(post.id)}
        />
        <div className="px-4 py-5">
          <div className="flex mb-4">
            <div className="flex items-center w-5/6">
              <div className="pr-2 items-">
                <img
                  className="h-8 w-8 object-contain border rounded-full"
                  src={post.creatorAvatarURL}
                  alt={post.userName}
                />
              </div>
              <div className="block">
                <Link
                  to={`/accounts/${post.accountId}`}
                  className="tracking-wide block text-sm"
                >
                  {post.userName}
                </Link>
                <time
                  dateTime={date.toString()}
                  className="block text-xs text-gray-500"
                >
                  {date.toLocaleDateString("en-AU").replaceAll("/", "-")}
                </time>
              </div>
            </div>
            <div className="flex items-center w-1/6">
              <div className="w-full relative">
                <span
                  className="flex items-center float-right space-x-1 hover:text-blue-600 text-gray-500 cursor-pointer"
                  aria-expanded={dropdown}
                  aria-haspopup="true"
                  onClick={() => setDropdown(!dropdown)}
                >
                  <FaEllipsisH />
                  {dropdown ? (
                    <PostCardMenu
                      owned={context.account.accountId === post.accountId}
                      postId={post.id}
                      dropdown={setDropdown}
                    />
                  ) : null}
                </span>
              </div>
            </div>
          </div>
          <p className="text-gray-500 text-base line-clamp-3 mb-2 whitespace-pre-line">
            {post.body}
          </p>
          <div className="flex mb-4">
            <div className="flex items-center space-x-1 mr-4 text-gray-400">
              <FaHeart size={14} />
              <small>{likes}</small>
            </div>
            <div className="flex items-center space-x-1 mr-4 text-gray-400">
              <FaComment size={14} />
              <small>{commentCount}</small>
            </div>
          </div>
          <hr className="mb-4" />
          <div className="flex">
            <div className="flex items-center w-5/6">
              <button
                className={`flex items-center space-x-1 hover:text-blue-600 mr-4 ${
                  liked ? "text-blue-600" : "text-gray-400"
                }`}
                onClick={handleLikeClick}
              >
                <FaHeart className="mr-1" />
                <small>{liked ? "Liked" : "Like"}</small>
              </button>
              <button
                className="flex items-center space-x-1 hover:text-blue-600 mr-4 text-gray-400"
                onClick={() => onClick(post.id)}
              >
                <FaComment className="mr-1" />
                <small>Comment</small>
              </button>
            </div>
            <div className="flex items-center w-1/6">
              <div className="w-full">
                <button
                  className="flex items-center float-right space-x-1 hover:text-blue-600 ml-4 text-gray-300 hidden"
                  title="Attribute a 2D file"
                  disabled={context.account.accountId !== post.accountId}
                  style={{
                    cursor:
                      context.account.accountId !== post.accountId
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={toggleShowModal}
                >
                  <FaEarthAmericas
                    color={
                      context.account.accountId !== post.accountId ? "grey" : ""
                    }
                  />
                </button>
                <button
                  className="flex items-center float-right space-x-1 hover:text-blue-600 ml-4 text-gray-300"
                  onClick={() => setShowMap(true)}
                >
                  <FaMapMarkerAlt />
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>
      <Modal
        show={modal?.show}
        component={modal?.component}
        onClose={modal?.onClose}
        size={modal?.size}
      />
    </>
  );
};

export const PostCardMenu = ({ postId, owned, dropdown }: IPostCardMenu) => {
  let navigate = useNavigate();

  const postService = new PostsV2Service();

  const content = useOutsideClick(() => {
    dropdown(false);
  });

  const handleDeleteClick = async () => {
    const confirm = await notifcation({
      title: "Confirm Delete",
      message: "Are your sure you want to delete this post?",
      cancel: "Cancel",
      confirm: "Delete",
    });

    if (confirm) {
      await postService.delete(postId).then(() => window.location.reload());
    }
  };

  const handleReportClick = async () => {
    const confirm = await notifcation({
      title: "Confirm Report",
      message: "Are you sure you want to report this post?",
      cancel: "Cancel",
      confirm: "Report",
    });

    if (confirm) {
      await postService.report(postId, "reason", "description");
    }
  };

  return (
    <div
      className="absolute right-0 z-10 w-56 top-5 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
      tabIndex={-1}
    >
      <div className="my-4" role="none" ref={content}>
        {owned ? (
          <>
            <button
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                navigate(getEditPostUrl(postId));
              }}
            >
              <FaPencilAlt size={14} className="mr-3" /> Edit post
            </button>
            {/*
            <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full" role="menuitem" tabIndex={-1}>
              <FaBookmark size={14} className="mr-3" /> Save as draft
            </button>
            */}
            <button
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
              role="menuitem"
              tabIndex={-1}
              onClick={handleDeleteClick}
            >
              <FaTrashAlt size={14} className="mr-3" /> Delete
            </button>
          </>
        ) : (
          <>
            {/*
            <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full" role="menuitem" tabIndex={-1}>
              <FaBookmark size={14} className="mr-3" /> Save
            </button>
            */}
            <button
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
              role="menuitem"
              tabIndex={-1}
              onClick={handleReportClick}
            >
              <FaExclamationTriangle size={14} className="mr-3" /> Report
            </button>
            {/*
            <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full" role="menuitem" tabIndex={-1}>
              <FaBullhorn size={14} className="mr-3" /> Get notified
            </button>
            <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full" role="menuitem" tabIndex={-1}>
              <FaUserTimes size={14} className="mr-3" /> Unfollow
            </button>
            */}
          </>
        )}
      </div>
    </div>
  );
};

interface IPostCard {
  post: IPost;
  onClick: (postId: string) => void;
  commentCount?: number;
}

interface IPostCardMenu {
  owned: boolean;
  postId: string;
  dropdown: (dropdown: boolean) => void;
}
